import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { optimisticScore, healthFor, ratingCounts } from "../lib/ratings";

// One assessment, for the editor.
//
// Rating an item is optimistic: the swatch and the score dial move immediately and the
// server's recomputed score replaces the guess when it lands. Without that the dial lags
// a network round-trip behind every click, and the editor is a screen someone sits in
// clicking through thirty-odd items.
// Rating an item by hand makes it the consultant's judgement. The server applies exactly
// this rule; deriving it here too keeps the Source column from reading "Not rated" for a
// row the user just rated, in the moment before the next full fetch.
const derived = (patch) => {
  if (!patch.rating || patch.source) return {};
  return { source: patch.rating === "na" ? "out_of_scope" : "consultant" };
};

// Out-of-scope items leave the score entirely, so they are excluded from a category's
// tally as well. Otherwise the category tab and the score band disagree on the same
// screen about how much is left to do.
const withCategoryCounts = (categories, items) =>
  (categories || []).map((c) => {
    const mine = items.filter((i) => i.category_id === c.id && i.rating !== "na");
    return { ...c, rated: mine.filter((i) => i.rating !== "unrated").length, total: mine.length };
  });

export function useAssessment(id) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      setData(await api.get(`s4/api/assessments/${id}`));
      setError("");
    } catch (e) {
      setError(e.message || "Could not load the assessment.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const rateItem = useCallback(async (itemId, patch) => {
    setError("");
    let rollback = null;

    setData((prev) => {
      if (!prev) return prev;
      rollback = prev;
      const items = prev.items.map((i) => (i.id === itemId ? { ...i, ...patch, ...derived(patch) } : i));
      const score = optimisticScore(items);
      return {
        ...prev,
        items,
        score,
        counts: ratingCounts(items),
        categories: withCategoryCounts(prev.categories, items),
        assessment: { ...prev.assessment, health: healthFor(score) },
      };
    });

    setSaving(true);
    try {
      const res = await api.patch(`s4/api/assessments/${id}/items/${itemId}`, patch);
      // The server's score is authoritative — it is what the list view and the client
      // deliverable will read.
      setData((prev) => prev && ({
        ...prev,
        score: res.score,
        counts: res.counts || prev.counts,
        assessment: { ...prev.assessment, health: res.health },
      }));
    } catch (e) {
      if (rollback) setData(rollback);
      setError(e.message || "That change was not saved.");
    } finally {
      setSaving(false);
    }
  }, [id]);

  const update = useCallback(async (patch) => {
    try {
      await api.patch(`s4/api/assessments/${id}`, patch);
      setData((prev) => prev && ({ ...prev, assessment: { ...prev.assessment, ...patch } }));
      return true;
    } catch (e) {
      setError(e.message || "Could not update the assessment.");
      return false;
    }
  }, [id]);

  // Structural edits reload rather than patching local state: adding items changes
  // positions and the score, and reconciling that by hand is how the two drift apart.
  const mutate = useCallback(async (fn, failure) => {
    setSaving(true); setError("");
    try { await fn(); await load(); return true; }
    catch (e) { setError(e.message || failure); return false; }
    finally { setSaving(false); }
  }, [load]);

  const addCategory = useCallback((name) =>
    mutate(() => api.post(`s4/api/assessments/${id}/categories`, { name }),
      "Could not add the category."), [id, mutate]);

  const renameCategory = useCallback((categoryId, name) =>
    mutate(() => api.patch(`s4/api/assessments/${id}/categories/${categoryId}`, { name }),
      "Could not rename the category."), [id, mutate]);

  const removeCategory = useCallback((categoryId) =>
    mutate(() => api.del(`s4/api/assessments/${id}/categories/${categoryId}`),
      "Could not remove the category."), [id, mutate]);

  const addItems = useCallback((categoryId, libraryItemIds) =>
    mutate(() => api.post(`s4/api/assessments/${id}/items`,
      { category_id: categoryId, library_item_ids: libraryItemIds }),
      "Could not add those items."), [id, mutate]);

  const removeItem = useCallback((itemId) =>
    mutate(() => api.del(`s4/api/assessments/${id}/items/${itemId}`),
      "Could not remove the item."), [id, mutate]);

  const reorderItems = useCallback((categoryId, itemIds) =>
    mutate(() => api.put(`s4/api/assessments/${id}/items/order`,
      { category_id: categoryId, item_ids: itemIds }),
      "Could not save the order."), [id, mutate]);

  return {
    ...(data || {}), loading, error, saving, reload: load, rateItem, update,
    addCategory, renameCategory, removeCategory, addItems, removeItem, reorderItems,
  };
}
